import deleteQuoteApi from '@/api/delete/quote/delete-quote';
import getQuotePdfApi from '@/api/get/dealer/get-quote-pdf';
import getQuotesApi from '@/api/get/dealer/get-quotes';
import closeQuoteApi from '@/api/patch/company/sales-flow/close-quote';
import Alert from '@/components/alert/alert-component';
import SubmitCard from '@/components/card/submit-card-component';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import { Col, Row } from '@/components/grid/grid-component';
import Modal from '@/components/modal/modal-component';
import PageWrapper from '@/components/page-wrapper/page-wrapper-component';
import Input from '@/components/react-hook-form-inputs/input/input-component';
import RadioButtonGroup from '@/components/react-hook-form-inputs/radio-button/radio-button-component';
import StatusIndicator from '@/components/status-indicator/status-indicator-component';
import Actions from '@/components/table/components/view-edit-delete/actions';
import Table from '@/components/table/table';
import Tooltip from '@/components/tooltip/tooltip-component';
import { useLazyQuery, useMutation } from '@/hooks/use-api';
import { APP_CONTEXT } from '@/utils/context';
import { Heading2Text } from '@/utils/global-fonts';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useCallback, useContext, useState } from 'react';
import {
  MdArrowForward,
  MdOutlineCopyAll,
  MdOutlineDelete,
  MdOutlineEdit,
  MdOutlineEmail,
  MdOutlineMore,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import Styled from './style';

const noWhiteSpace = { whiteSpace: 'nowrap' };

const renderStatus = ({ row }) => {
  if (
    (row.original.status === 'sold' || row.original.status === 'rejected') &&
    row.original.rejected_reason
  ) {
    return (
      <Tooltip
        text={row.original.rejected_reason}
        id={`${row.original.id}-status-tooltip`}
        icon={<StatusIndicator status={row.original.status.toLowerCase()} />}
      />
    );
  }
  return <StatusIndicator status={row.original.status.toLowerCase()} />;
};

const modalParentRowStyle = {
  marginLeft: '0px',
  marginRight: '0px',
  marginBottom: '0px',
  padding: '11px',
};

const radioContainerStyle = {
  marginBottom: '10px',
};

const CloseQuoteModal = ({ open, onClose, onSubmit }) => (
  <Modal
    width="500px"
    open={open}
    height="auto"
    showClose
    bodyStyle={{
      padding: '0px',
    }}
    close={() => {
      onClose();
    }}
    title="Close Quote"
  >
    <FormWrapper onSubmit={(values) => onSubmit(values, open.id)}>
      <Row style={modalParentRowStyle}>
        <Col xs={12} style={radioContainerStyle}>
          <RadioButtonGroup
            options={[
              { value: 'sold', label: 'Mark as Sold' },
              { value: 'rejected', label: 'Reject Quote' },
            ]}
            hideLabel
            name="status"
            registerOptions={{
              required: 'Please select a status',
            }}
          />
        </Col>
        <Col xs={12}>
          <Input name="rejected_reason" label="Reason" textArea />
        </Col>
      </Row>
      <SubmitCard
        padding="0"
        primaryButtonTitle="Submit"
        primaryButtonType="submit"
        primaryButtonWidth="auto"
        primaryButtonDisabled={false}
        primaryButtonLoading={false}
        secondaryButtonTitle="Cancel"
        submitClickSecondary={onClose}
      />
    </FormWrapper>
  </Modal>
);

CloseQuoteModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

/**
 * User Management page component.
 * @returns {JSX.Element} The rendered User Management page component.
 */
const QuoteManagement = () => {
  const navigate = useNavigate();
  const { currentUser } = useContext(APP_CONTEXT);
  const { data, refetch, error, loading, count } = useLazyQuery(getQuotesApi);
  const [openCloseQuoteModal, setOpenCloseQuoteModal] = useState(false);
  const [deleteQuote, { loading: deleteQuoteLoading }] =
    useMutation(deleteQuoteApi);
  const [updateQuote, { loading: updateQuoteLoading }] =
    useMutation(closeQuoteApi);

  const launchDeleteConfirmation = useCallback(
    (quoteData) => {
      ConfirmationModal({
        title: 'Delete Quote',
        text: `Are you sure you want to delete this quote?`,
        onConfirm: () =>
          deleteQuote(quoteData?.original?.id).then(() => {
            Alert({
              icon: 'success',
              text: 'Quote deleted successfully',
            });
            refetch();
          }),
      });
    },
    [deleteQuote, refetch],
  );

  const renderActions = useCallback(
    ({ row }) => {
      const quoteDetails = row.original;
      const iconStyle = { marginRight: '10px' };

      const emailAction = {
        text: 'Email',
        icon: <MdOutlineEmail style={iconStyle} />,
        onClick: () => {
          Alert({
            icon: 'info',
            message: 'Sending quote please wait.',
          });
          const canQuote = currentUser?.quoting_function_access;
          let type = row.original.type ?? 'quote';
          if (!canQuote) {
            type = 'enquiry';
          }
          getQuotePdfApi(row.original.id, type)
            .then(() => {
              Alert({
                icon: 'success',
                message: 'Quote sent to your inbox',
              });
            })
            .catch(() => {
              Alert({
                icon: 'error',
                message: 'Failed to send quote',
              });
            });
        },
      };

      const additionalActions = [];
      if (quoteDetails.status === 'configuration') {
        additionalActions.push({
          text: 'Delete',
          textColor: '#FF5353',
          icon: <MdOutlineDelete style={iconStyle} color="#FF5353" />,
          onClick: () => launchDeleteConfirmation(row),
        });
      }

      if (quoteDetails.status === 'quoted') {
        additionalActions.push({
          text: 'Close Quote',
          icon: <MdOutlineMore style={iconStyle} />,
          onClick: () => setOpenCloseQuoteModal(quoteDetails),
        });
        additionalActions.push(emailAction);
      }

      let actions = [
        {
          text: 'View Summary',
          icon: <MdArrowForward style={iconStyle} />,
          onClick: () =>
            navigate(`/shop/sales/main-summary/${quoteDetails.id}/summary`, {
              state: {
                quoteManagement: true,
                selectedProduct: {
                  id: quoteDetails.product_id,
                  name: quoteDetails.product_name,
                },
                selectedProductCategory: {
                  id: quoteDetails.product_category_id,
                  name: quoteDetails.product_category_name,
                },
                selectedSupplier: {
                  id: quoteDetails.company_id,
                  name: quoteDetails.company_name,
                },
                quoteId: quoteDetails.id,
              },
            }),
        },
        {
          text: 'Edit',
          icon: <MdOutlineEdit style={iconStyle} />,
          onClick: () =>
            navigate('/shop/sales/configuration', {
              state: {
                selectedProduct: {
                  id: quoteDetails.product_id,
                  name: quoteDetails.product_name,
                },
                selectedProductCategory: {
                  id: quoteDetails.product_category_id,
                  name: quoteDetails.product_category_name,
                },
                selectedSupplier: {
                  id: quoteDetails.company_id,
                  name: quoteDetails.company_name,
                },
                quoteId: quoteDetails.id,
              },
            }),
        },
        {
          text: 'Duplicate Quote',
          icon: <MdOutlineCopyAll style={iconStyle} />,
          onClick: () =>
            navigate('/shop/sales/configuration', {
              state: {
                selectedProduct: {
                  id: quoteDetails.product_id,
                  name: quoteDetails.product_name,
                },
                selectedProductCategory: {
                  id: quoteDetails.product_category_id,
                  name: quoteDetails.product_category_name,
                },
                selectedSupplier: {
                  id: quoteDetails.company_id,
                  name: quoteDetails.company_name,
                },
                quoteId: quoteDetails.id,
                duplicate: true,
              },
            }),
        },
        ...additionalActions,
      ];

      if (['sold', 'rejected'].includes(quoteDetails.status)) {
        actions = [
          {
            text: 'New Quote',
            icon: <MdArrowForward style={iconStyle} />,
            onClick: () =>
              navigate('/shop/sales/configuration', {
                state: {
                  selectedProduct: {
                    id: quoteDetails.product_id,
                    name: quoteDetails.product_name,
                  },
                  selectedProductCategory: {
                    id: quoteDetails.product_category_id,
                    name: quoteDetails.product_category_name,
                  },
                  selectedSupplier: {
                    id: quoteDetails.company_id,
                    name: quoteDetails.company_name,
                  },
                  quoteId: quoteDetails.id,
                  duplicate: true,
                },
              }),
          },
          emailAction,
        ];
      }

      return <Actions actions={actions} />;
    },
    [currentUser?.quoting_function_access, launchDeleteConfirmation, navigate],
  );

  const renderCreatedAt = ({ row }) => {
    if (row.original.created_at) {
      const date = row.original.created_at;
      const formattedDate = dayjs(date).format('DD/MM/YYYY');
      const formattedTime = dayjs(date).format('hh:mm');
      return <div>{`${formattedDate} | ${formattedTime}`}</div>;
    }
    return <div />;
  };

  const renderUpdatedAt = ({ row }) => {
    if (row.original.updated_at) {
      const date = row.original.created_at;
      const formattedDate = dayjs(date).format('DD/MM/YYYY');
      const formattedTime = dayjs(date).format('hh:mm');
      return <div>{`${formattedDate} | ${formattedTime}`}</div>;
    }
    return <div />;
  };

  const filters = [
    {
      header: 'Status:',
      accessor: 'status',
      placeholder: 'Please select a Status',
      type: 'select',
      options: [
        { value: 'configuration', text: 'Configuration' },
        { value: 'quoting', text: 'Quoting' },
        { value: 'quoted', text: 'Quoted' },
        { value: 'rejected', text: 'Rejected' },
        { value: 'sold', text: 'Sold' },
        { value: 'inactive', text: 'Inactive' },
      ],
    },
  ];

  const QuoteColumns = [
    {
      Header: 'QUOTE NUMBER',
      id: 'quote_number',
      accessor: 'quote_number',
      search: true,
    },
    {
      Header: 'DATE ADDED',
      id: 'created_at',
      accessor: 'created_at',
      search: true,
      Cell: (props) => renderCreatedAt(props),
    },
    {
      Header: 'LAST UPDATED',
      id: 'updated_at',
      accessor: 'updated_at',
      search: true,
      Cell: (props) => renderUpdatedAt(props),
    },
    {
      Header: 'SUPPLIER',
      id: 'company_name',
      accessor: 'company_name',
      search: true,
    },
    {
      Header: 'PRODUCT',
      id: 'product_name',
      accessor: 'product_name',
      search: true,
    },
    {
      Header: 'PRODUCT CATEGORY',
      id: 'product_category_name',
      accessor: 'product_category_name',
      search: true,
    },
    {
      Header: 'Price (Total excl Vat)',
      id: 'total_cost',
      accessor: 'total_cost',
      search: true,
    },
    {
      Header: 'CUSTOMER',
      id: 'customer_name',
      accessor: 'customer_name',
      search: true,
    },
    {
      Header: 'STATUS',
      id: 'status',
      accessor: 'status',
      search: true,
      Cell: renderStatus,
    },
    {
      Header: 'ACTIONS',
      id: 'actions',
      Cell: (props) => renderActions(props),
    },
  ];

  return (
    <PageWrapper>
      <Styled.MainPageContainer>
        <Styled.Heading>
          <Heading2Text style={noWhiteSpace}>Quote Management</Heading2Text>
        </Styled.Heading>
        <Table
          columns={QuoteColumns}
          data={data ?? []}
          tableOptions={['search', 'filter', 'columns']}
          id="quote-list"
          defaultSort="quote_number:DESC"
          dataCount={count ?? 0}
          downloadAccessor={data || []}
          error={error}
          loading={deleteQuoteLoading || updateQuoteLoading || loading}
          filters={filters}
          refetch={refetch}
          getDownloadData={getQuotesApi}
        />
        <CloseQuoteModal
          open={openCloseQuoteModal}
          onClose={() => setOpenCloseQuoteModal(false)}
          onSubmit={(values, id) => {
            updateQuote(id, values)
              .then(() => {
                Alert({
                  icon: 'success',
                  text: 'Quote closed successfully',
                });
                refetch();
              })
              .catch(() => {
                Alert({
                  icon: 'error',
                  text: 'Failed to close quote',
                });
              });
            setOpenCloseQuoteModal(false);
          }}
        />
      </Styled.MainPageContainer>
    </PageWrapper>
  );
};

export default QuoteManagement;
